import { useUpdateTableViewCellApi } from '@/api/tableViews';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ExpandableText } from '@/components/ui/expandable-text';
import { FlexCol, FlexRow } from '@/components/ui/flex';
import { Input } from '@/components/ui/input';
import { Multiselect } from '@/components/ui/multiselect';
import { Text } from '@/components/ui/text';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { CitationHoverModal } from '@/pages/Chat/components/Chat/CitationHoverModal';
import { isValidUrl, normalizeUrl } from '@/utils/urlUtils';
import type {
  TableViewCellDto,
  TableViewColumnDto,
  TableViewRowDto,
} from '@ditto/shared';
import { Check, Info, Pencil, Stars } from 'lucide-react';
import { useEffect, useState, type FC } from 'react';
import { columnTypeIcon, getColorForOption } from '../../../utils/cellUtils';

type CellDetailsProps = {
  cell: TableViewCellDto;
  column: TableViewColumnDto;
  columns: TableViewColumnDto[];
  row: TableViewRowDto;
  tableViewId: string;
};

export const OptionCellDetails: FC<CellDetailsProps> = ({
  cell,
  column,
  columns,
  row,
  tableViewId,
}) => {
  const { mutate: updateCell } = useUpdateTableViewCellApi();

  // Optimistic local state for selected options
  const [localSelectedOptions, setLocalSelectedOptions] = useState(
    cell.selectedOptions ?? []
  );

  // Sync local state when cell data changes from server
  useEffect(() => {
    setLocalSelectedOptions(cell.selectedOptions ?? []);
  }, [cell.selectedOptions]);

  const sourceName = row.fileName ?? row.label;
  const hasRowFile = Boolean(row.fileId);
  const availableOptions = column.options ?? [];

  const multiselectOptions = availableOptions.map(option => ({
    label: option.value,
    value: option.id,
    color: getColorForOption(option.id, columns),
  }));

  const selectedValues = localSelectedOptions.map(opt => opt.id);

  const handleOptionToggle = (optionId: string) => {
    const isSelected = localSelectedOptions.some(opt => opt.id === optionId);

    // Calculate new selected options
    const newSelectedOptions = isSelected
      ? localSelectedOptions.filter(opt => opt.id !== optionId)
      : [
          ...localSelectedOptions,
          availableOptions.find(opt => opt.id === optionId)!,
        ].filter(Boolean);

    const newSelectedOptionIds = newSelectedOptions.map(opt => opt.id);

    // Optimistic update - immediately update local state
    setLocalSelectedOptions(newSelectedOptions);

    // Trigger backend update
    updateCell({
      tableViewId,
      data: {
        rowId: row.id,
        columnId: column.id,
        patch: {
          selectedOptionIds: newSelectedOptionIds,
        },
      },
    });
  };

  const handleChange = (newSelectedValues: string[]) => {
    const added = newSelectedValues.find(id => !selectedValues.includes(id));
    const removed = selectedValues.find(id => !newSelectedValues.includes(id));
    const toggledId = added || removed;
    if (toggledId) {
      handleOptionToggle(toggledId);
    }
  };

  const dropdownTrigger = (
    <Button variant="ghost" size="icon">
      <Pencil className="w-4 h-4 text-neutral-900" />
    </Button>
  );

  const IconComponent = columnTypeIcon(column.type);

  return (
    <FlexCol gap="4">
      <FlexCol gap="1.5">
        <FlexRow justifyContent="between" alignItems="center">
          <FlexRow alignItems="center" gap="1.5" className="h-[18px]">
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="flex items-center">
                  {IconComponent && (
                    <IconComponent className="w-3.5 h-3.5 text-neutral-900 opacity-50" />
                  )}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {column.type.charAt(0).toUpperCase() + column.type.slice(1)}
              </TooltipContent>
            </Tooltip>

            <Text className="text-[11px] font-medium text-neutral-600 uppercase tracking-[1px]">
              {column.name}
            </Text>

            <Tooltip>
              <TooltipTrigger asChild>
                <span className="flex items-center">
                  <Info className="w-3.5 h-3.5 text-neutral-900 opacity-50" />
                </span>
              </TooltipTrigger>
              <TooltipContent>
                The instructions provided for this column are: {column.prompt}
              </TooltipContent>
            </Tooltip>
          </FlexRow>

          <Multiselect
            options={multiselectOptions}
            selected={selectedValues}
            onChange={handleChange}
            trigger={dropdownTrigger}
            showClearAll={false}
            align="end"
          />
        </FlexRow>

        {localSelectedOptions.length > 0 ? (
          <FlexRow gap="1" className="flex-wrap">
            {localSelectedOptions.map(option => (
              <Badge
                key={option.id}
                variant="outline"
                className="w-fit h-6 rounded-lg border-none"
                color={getColorForOption(option.id, columns)}
              >
                {option.value}
              </Badge>
            ))}
          </FlexRow>
        ) : (
          <Text className="text-base font-medium text-neutral-400 leading-normal">
            No value
          </Text>
        )}
      </FlexCol>

      {cell?.motivation && (
        <FlexCol gap="1">
          <FlexRow alignItems="center" gap="1.5">
            <Stars className="w-3.5 h-3.5 text-magenta-500" />
            <Text className="text-[11px] font-medium text-neutral-600 uppercase tracking-[1px]">
              AI Reasoning
            </Text>
          </FlexRow>
          <ExpandableText className="text-neutral-600">
            {cell.motivation}
          </ExpandableText>
          {cell.quote && hasRowFile && (
            <CitationHoverModal
              type="file"
              sourceName={sourceName ?? ''}
              quotedText={cell.quote}
            >
              <div className="w-5 h-5 bg-yellow-400 text-black rounded-sm flex items-center justify-center text-xs font-medium shrink-0 cursor-pointer">
                1
              </div>
            </CitationHoverModal>
          )}
        </FlexCol>
      )}
    </FlexCol>
  );
};

// Helper to extract the raw value from a cell based on column type
const getCellRawValue = (
  cell: TableViewCellDto,
  column: TableViewColumnDto
): string | number | null => {
  if (column.type === 'text') return cell.textValue ?? null;
  if (column.type === 'number') return cell.numberValue ?? null;
  if (column.type === 'date') return cell.dateValue ?? null;
  if (column.type === 'verbatim') return cell.verbatimValue ?? null;
  return null;
};

export const NormalCellDetails: FC<CellDetailsProps> = ({
  cell,
  column,
  row,
  tableViewId,
}) => {
  const { mutate: updateCell } = useUpdateTableViewCellApi();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  // Optimistic local state for cell value
  const [localCellValue, setLocalCellValue] = useState<string | number | null>(
    () => getCellRawValue(cell, column)
  );

  // Sync local state when cell data changes from server
  useEffect(() => {
    setLocalCellValue(getCellRawValue(cell, column));
  }, [cell, column]);

  const sourceName = row.fileName ?? row.label;
  const hasRowFile = Boolean(row.fileId);

  const getCurrentValueForEdit = () => {
    if (column.type === 'text') return (localCellValue as string) ?? '';
    if (column.type === 'number')
      return localCellValue != null ? String(localCellValue) : '';
    if (column.type === 'date')
      return localCellValue
        ? new Date(localCellValue as string).toISOString().split('T')[0]
        : '';
    if (column.type === 'verbatim') return (localCellValue as string) ?? '';
    return '';
  };

  const handleEditClick = () => {
    setEditValue(getCurrentValueForEdit());
    setValidationError(null);
    setIsEditing(true);
  };

  const validateValue = (value: string): boolean => {
    if (column.type === 'number') {
      const num = parseFloat(value);
      if (value.trim() !== '' && (isNaN(num) || !isFinite(num))) {
        setValidationError('Please enter a valid number');
        return false;
      }
    }

    if (column.type === 'date') {
      const date = new Date(value);
      if (value.trim() !== '' && isNaN(date.getTime())) {
        setValidationError('Please enter a valid date');
        return false;
      }
    }

    setValidationError(null);
    return true;
  };

  const handleSaveClick = () => {
    if (!validateValue(editValue)) {
      return;
    }

    const patch: {
      textValue?: string | null;
      numberValue?: number | null;
      dateValue?: string | null;
      verbatimValue?: string | null;
    } = {};

    let newLocalValue: string | number | null = null;

    if (column.type === 'text') {
      newLocalValue = editValue.trim() === '' ? null : editValue;
      patch.textValue = newLocalValue;
    } else if (column.type === 'number') {
      newLocalValue = editValue.trim() === '' ? null : parseFloat(editValue);
      patch.numberValue = newLocalValue;
    } else if (column.type === 'date') {
      newLocalValue = editValue.trim() === '' ? null : editValue;
      patch.dateValue = newLocalValue;
    } else if (column.type === 'verbatim') {
      newLocalValue = editValue.trim() === '' ? null : editValue;
      patch.verbatimValue = newLocalValue;
    }

    // Optimistic update - immediately update local state and close editor
    setLocalCellValue(newLocalValue);
    setIsEditing(false);

    // Trigger backend update
    updateCell(
      {
        tableViewId,
        data: {
          rowId: row.id,
          columnId: column.id,
          patch,
        },
      },
      {
        onError: error => {
          // Revert to server value on error
          setLocalCellValue(getCellRawValue(cell, column));
          setValidationError(
            error instanceof Error ? error.message : 'Failed to update cell'
          );
          setIsEditing(true);
          setEditValue(editValue);
        },
      }
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSaveClick();
    }
  };

  const IconComponent = columnTypeIcon(column.type);

  // Build display value from local state
  const getLocalDisplayValue = (): string | undefined => {
    if (localCellValue == null) return undefined;
    if (column.type === 'text') return localCellValue as string;
    if (column.type === 'number') return String(localCellValue);
    if (column.type === 'date')
      return new Date(localCellValue as string).toLocaleDateString();
    if (column.type === 'verbatim') return `"${localCellValue}"`;
    return undefined;
  };

  const displayValue = getLocalDisplayValue();

  return (
    <FlexCol gap="4">
      <FlexCol gap="1.5">
        <FlexRow justifyContent="between" alignItems="center">
          <FlexRow alignItems="center" gap="1.5" className="h-[18px]">
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="flex items-center">
                  {IconComponent && (
                    <IconComponent className="w-3.5 h-3.5 text-neutral-900 opacity-50" />
                  )}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {column.type.charAt(0).toUpperCase() + column.type.slice(1)}
              </TooltipContent>
            </Tooltip>

            <Text className="text-[11px] font-medium text-neutral-600 uppercase tracking-[1px]">
              {column.name}
            </Text>

            <Tooltip>
              <TooltipTrigger asChild>
                <span className="flex items-center">
                  <Info className="w-3.5 h-3.5 text-neutral-900 opacity-50" />
                </span>
              </TooltipTrigger>
              <TooltipContent>
                The instructions provided for this column are: {column.prompt}
              </TooltipContent>
            </Tooltip>
          </FlexRow>
          <Button
            variant="ghost"
            size="icon"
            onClick={isEditing ? handleSaveClick : handleEditClick}
          >
            {isEditing ? (
              <Check className="w-4 h-4 text-neutral-900" />
            ) : (
              <Pencil className="w-4 h-4 text-neutral-900" />
            )}
          </Button>
        </FlexRow>

        {isEditing ? (
          <FlexCol gap="2">
            {column.type === 'date' ? (
              <Input
                type="date"
                value={editValue}
                onChange={e => setEditValue(e.target.value)}
                className="w-full"
              />
            ) : column.type === 'number' ? (
              <input
                type="number"
                value={editValue}
                onChange={e => setEditValue(e.target.value)}
                onKeyDown={handleKeyDown}
                className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400"
                step="any"
              />
            ) : (
              <Textarea
                value={editValue}
                onChange={e => setEditValue(e.target.value)}
                onKeyDown={handleKeyDown}
                className="w-full min-h-[80px] px-3 py-2 text-sm border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400"
                placeholder="Enter value..."
              />
            )}
            {validationError && (
              <Text className="text-sm text-red-600">{validationError}</Text>
            )}
          </FlexCol>
        ) : displayValue ? (
          isValidUrl(displayValue) ? (
            <Text variant="body-sm" className="text-blue-600 hover:underline">
              <a
                href={normalizeUrl(displayValue)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline font-medium break-all"
              >
                {displayValue}
              </a>
            </Text>
          ) : (
            <ExpandableText className="font-medium text-black">
              {displayValue || '-'}
            </ExpandableText>
          )
        ) : (
          <Text className="text-sm font-medium text-neutral-400 leading-normal">
            No value extracted
          </Text>
        )}
      </FlexCol>

      {cell?.motivation && (
        <FlexCol gap="1">
          <FlexRow alignItems="center" gap="1.5">
            <Stars className="w-3.5 h-3.5 text-magenta-500" />
            <Text className="text-[11px] font-medium text-neutral-600 uppercase tracking-[1px]">
              AI Reasoning
            </Text>
          </FlexRow>
          <ExpandableText className="text-neutral-600">
            {cell.motivation}
          </ExpandableText>
          {cell.quote && hasRowFile && (
            <CitationHoverModal
              type="file"
              sourceName={sourceName ?? ''}
              quotedText={cell.quote}
            >
              <div className="w-5 h-5 bg-yellow-400 text-black rounded-sm flex items-center justify-center text-xs font-medium shrink-0 cursor-pointer">
                1
              </div>
            </CitationHoverModal>
          )}
        </FlexCol>
      )}
    </FlexCol>
  );
};
